import { useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import styled from '@/styles';
import { dependabilityV2Colors } from '@/styles/colors';
import ActivityFeed from './ActivityFeed';
import useDependabilityEvents from './useDependabilityEvents';
import { XMarkIcon } from './icons';

// "View more" target from the view-activities frame (37920-11754): a 640px
// "Recent activity" dialog listing the event history, closed by an outlined
// "Go back" button in a top-bordered footer.
const Footer = styled('div', {
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '16px',
  borderTop: `1px solid ${dependabilityV2Colors.depBorder}`,
});
const GoBack = styled('button', {
  minWidth: '100px',
  height: '36px',
  padding: '8px 16px',
  borderRadius: '8px',
  border: `1px solid ${dependabilityV2Colors.depFeedPositive}`,
  backgroundColor: 'white',
  color: dependabilityV2Colors.depPositive,
  fontSize: '$md',
  fontWeight: 600,
  cursor: 'pointer',
});

const PER_PAGE = 20;

interface Props {
  workerId: number;
  isOpen: boolean;
  onClose: () => void;
}

const RecentActivityModal = ({ workerId, isOpen, onClose }: Props) => {
  const { events, page, loading, loaded, error, loadPage, retry, hasMore } =
    useDependabilityEvents(workerId, PER_PAGE);

  useEffect(() => {
    if (isOpen) loadPage(1);
  }, [isOpen, loadPage]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 640,
          maxWidth: '95vw',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
        },
      }}
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' } },
      }}
    >
      <DialogTitle
        sx={{
          padding: '16px 16px 8px',
          fontSize: 16,
          fontWeight: 600,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        Recent activity
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{ color: dependabilityV2Colors.depInk }}
        >
          <XMarkIcon />
        </IconButton>
      </DialogTitle>
      {/* rows carry their own 16px bottom padding (design row template), which
          provides the clearance to the footer border */}
      <DialogContent sx={{ padding: '0 16px' }}>
        <ActivityFeed
          events={events}
          hasMore={hasMore}
          // "not fetched yet" renders as loading, or the empty note flashes
          // for the paint between open and the fetch effect
          loading={loading || !loaded}
          error={error}
          // Retry the page that was actually last ATTEMPTED (fix round 5): the
          // events.length/page heuristic mis-targets after a close/reopen whose
          // page-1 refresh fails (stale prior-session page -> appends page N+1
          // instead of refreshing page 1). retry() reads lastAttemptedPageRef.
          onRetry={retry}
          onViewMore={() => loadPage(page + 1)}
          withHeader={false}
        />
      </DialogContent>
      <Footer>
        <GoBack type="button" onClick={onClose}>
          Go back
        </GoBack>
      </Footer>
    </Dialog>
  );
};

export default RecentActivityModal;
